import { Link } from '@tanstack/react-router';
import type { MissingBankInfoEmployee } from '@kora/types';

/**
 * Shown when a bank-file download is blocked (422 EXPORT_BLOCKED_MISSING_BANK_INFO).
 * Lists the employees missing bank account numbers, with deep-links to their
 * profiles. Tenant-side light styling, mirroring UpgradeContactModal's shell.
 */
export function MissingBankInfoModal({
  missingEmployees,
  currency,
  onClose,
}: {
  missingEmployees: MissingBankInfoEmployee[];
  currency: string;
  onClose: () => void;
}): JSX.Element {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="missing-bank-info-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-md w-full mx-4 border shadow-lg text-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="missing-bank-info-title" className="text-xl font-semibold mb-2">
          Cannot generate {currency} bank file
        </h2>
        <p className="text-sm text-gray-700 mb-3">
          {missingEmployees.length} employee{missingEmployees.length === 1 ? '' : 's'} paid in {currency}{' '}
          {missingEmployees.length === 1 ? 'is' : 'are'} missing a bank account number. Update their profiles, then try
          again.
        </p>
        <ul className="mb-4 max-h-60 overflow-y-auto divide-y divide-gray-100 border rounded-md">
          {missingEmployees.map((emp) => (
            <li key={emp.id} className="px-3 py-2 text-sm">
              <Link
                to="/employees/$id"
                params={{ id: emp.id }}
                onClick={onClose}
                className="text-indigo-primary hover:underline"
              >
                {emp.fullName} ({emp.employeeNumber})
              </Link>
            </li>
          ))}
        </ul>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
